import axios from "axios";
import { API_CONFIG } from "../config/api.config";
import { APP_URLS } from "../config/urls.config";

const BASE_URL = API_CONFIG.BASE_URL;

// Add axios interceptors for better error handling
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("API Error:", {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data,
    });
    return Promise.reject(error);
  }
);

// Hotel/Room Booking APIs
export const hotelBookingAPI = {
  // Create hotel booking with payment
  createBookingWithPayment: async (bookingData) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/room-bookings/create-with-payment`,
        bookingData,
        {
          params: {
            verifyBaseUrl: APP_URLS.BOOKING_VERIFY_QR
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Create hotel booking failed:", error);
      throw new Error(
        error.response?.data?.error || "Failed to create hotel booking"
      );
    }
  },

  // Verify hotel booking payment
  verifyPayment: async (bookingId, paymentData) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/room-bookings/${bookingId}/verify-payment`,
        paymentData,
        {
          params: {
            verifyBaseUrl: APP_URLS.BOOKING_VERIFY_QR
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Hotel payment verification failed:", error);
      throw new Error(
        error.response?.data?.error || "Payment verification failed"
      );
    }
  },

  // Get user's hotel bookings
  getUserBookings: async (userId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/room-bookings/user/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error("Get user hotel bookings failed:", error);
      throw new Error(
        error.response?.data?.error || "Failed to get hotel bookings"
      );
    }
  },

  // Verify payment and create booking
  verifyPaymentAndCreate: async (verificationData) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/room-bookings/verify-payment-and-create`,
        verificationData,
        {
          params: {
            verifyBaseUrl: APP_URLS.BOOKING_VERIFY_QR
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Hotel payment verification and creation failed:", error);
      throw new Error(
        error.response?.data?.error || "Payment verification and creation failed"
      );
    }
  },

  // Cancel hotel booking
  cancelBooking: async (bookingId) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/room-bookings/cancel/${bookingId}`
      );
      return response.data;
    } catch (error) {
      console.error("Cancel hotel booking failed:", error);
      throw new Error(
        error.response?.data?.message || "Failed to cancel hotel booking"
      );
    }
  },
};

// Restaurant Booking APIs
export const restaurantBookingAPI = {
  // Create restaurant booking with payment
  createBookingWithPayment: async (bookingData) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/restaurant-bookings/create-with-payment`,
        bookingData,
        {
          params: {
            verifyBaseUrl: APP_URLS.BOOKING_VERIFY_QR
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Create restaurant booking failed:", error);
      throw new Error(
        error.response?.data?.error || "Failed to create restaurant booking"
      );
    }
  },

  // Verify restaurant booking payment
  verifyPayment: async (bookingId, paymentData) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/restaurant-bookings/${bookingId}/verify-payment`,
        paymentData,
        {
          params: {
            verifyBaseUrl: APP_URLS.BOOKING_VERIFY_QR
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Restaurant payment verification failed:", error);
      throw new Error(
        error.response?.data?.error || "Payment verification failed"
      );
    }
  },

  // Get user's restaurant bookings
  getUserBookings: async (userId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/restaurant-bookings/user/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error("Get user restaurant bookings failed:", error);
      throw new Error(
        error.response?.data?.error || "Failed to get restaurant bookings"
      );
    }
  },

  // Verify payment and create restaurant booking
  verifyPaymentAndCreate: async (verificationData) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/restaurant-bookings/verify-payment-and-create`,
        verificationData,
        {
          params: {
            verifyBaseUrl: APP_URLS.BOOKING_VERIFY_QR
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Restaurant payment verification and creation failed:", error);
      throw new Error(
        error.response?.data?.error || "Payment verification and creation failed"
      );
    }
  },

  // Cancel restaurant booking
  cancelBooking: async (bookingId) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/restaurant-bookings/${bookingId}/cancel`
      );
      return response.data;
    } catch (error) {
      console.error("Cancel restaurant booking failed:", error);
      throw new Error(
        error.response?.data?.message || "Failed to cancel restaurant booking"
      );
    }
  },
};

// Legacy booking functions (keeping for backward compatibility)
export const createBooking = async (bookingData) => {
  try {
    const response = await axios.post(`${BASE_URL}/bookings`, bookingData);
    return response.data;
  } catch (error) {
    console.error("Create booking failed:", error);
    throw new Error(
      error.response?.data?.message || "Failed to create booking"
    );
  }
};

export const cancelBooking = async (id) => {
  try {
    const response = await axios.post(`${BASE_URL}/bookings/${id}/cancel`);
    return response.data;
  } catch (error) {
    console.error("Cancel booking failed:", error);
    throw new Error(
      error.response?.data?.message || "Failed to cancel booking"
    );
  }
};

export const getBookingsByUserId = async (userId) => {
  try {
    const response = await axios.get(`${BASE_URL}/bookings/user/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Get user bookings failed:", error);
    throw new Error(
      error.response?.data?.message || "Failed to get user bookings"
    );
  }
};

// Get Available Rooms with error handling
export const getAvailableRooms = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/available-rooms`);
    return response.data;
  } catch (error) {
    console.error("Get available rooms failed:", error);
    throw new Error(
      error.response?.data?.message || "Failed to get available rooms"
    );
  }
};

// Payment utility functions
export const paymentUtils = {
  // Initialize Razorpay payment
  initializeRazorpayPayment: (options, onSuccess, onError) => {
    if (!window.Razorpay) {
      console.error("Razorpay SDK not loaded");
      onError("Payment gateway not available");
      return;
    }

    const razorpayOptions = {
      ...options,
      handler: (response) => {
        onSuccess(response);
      },
      modal: {
        ondismiss: () => {
          onError("Payment cancelled by user");
        },
      },
    };

    const rzp = new window.Razorpay(razorpayOptions);
    rzp.open();
  },

  // Format amount for display
  formatAmount: (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  },
};

// Health check endpoint
export const checkApiConnection = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/available-rooms`);
    return response.status === 200;
  } catch (error) {
    console.error("API connection check failed:", error);
    return false;
  }
};
